import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/store/reduxHooks';
import { type ISocketOrderEvent } from '@/types/ISocketOrderEvent';
import { getOrderToastSummary } from '@/utils/getOrderToastSummary';
import {
  isNativeNotificationSupported,
  getNativePermission,
  showNativeNotification
} from '@/services/nativeNotifications/nativeNotificationService';

/**
 * Собирает тело баннера из непустых полей сводки: «получатель · город». Суммы и
 * товара нет с PAR-5189 — событие приходит без позиций.
 */
const buildBody = (
  summary: ReturnType<typeof getOrderToastSummary>
): string => {
  const parts = [summary.recipientName, summary.city].filter(
    (part): part is string => part !== null && part !== ''
  );

  return parts.join(' · ');
};

/**
 * Показ нативного ОС-уведомления о заказе. Возвращает функцию `notify`, которую
 * дёргает `MercureEventsListener` на каждое событие. Баннер показывается только
 * когда вкладка не видна (в фокусе — наш тост), браузер дал разрешение и
 * пользователь не выключил мягкий тумблер. Клик по баннеру навигирует на заказ
 * (пометку просмотренным делает страница заказа, как при клике по тосту).
 *
 * @returns функция показа баннера для события заказа
 */
export const useNativeOrderNotification = (): ((
  event: ISocketOrderEvent
) => void) => {
  const navigate = useNavigate();
  const enabled = useAppSelector(
    (state) => state.nativeNotificationSettings.enabled
  );

  return useCallback(
    (event: ISocketOrderEvent) => {
      const allowed =
        isNativeNotificationSupported() &&
        getNativePermission() === 'granted' &&
        enabled &&
        document.visibilityState !== 'visible';

      if (!allowed) {
        return;
      }

      const summary = getOrderToastSummary(event.data);

      showNativeNotification({
        title: `Новый заказ ${summary.title}`,
        body: buildBody(summary),
        tag: summary.uuid,
        onClick: () => {
          void navigate(summary.orderPath);
        }
      });
    },
    [enabled, navigate]
  );
};
